import type { FieldValues, UseFormStateProps, UseFormStateReturn } from '../types';

import { useFormContext } from './use-form-context.hook';

/**
 * This custom hook allows you to subscribe to each form state, and isolate the re-render at the custom hook level. It has its scope in terms of form state subscription, so it would not affect other useFormState and useForm. Using this hook can reduce the re-render impact on large and complex form application.
 *
 * @remarks
 * [API](https://reacty-form.com/docs/useformstate) • [Demo](https://codesandbox.io/s/useformstate-75xly)
 *
 * @param props - include options on specify fields to subscribe. {@link UseFormStateReturn}
 *
 * @example
 * ```tsx
 * function App() {
 *   const { register, handleSubmit, control } = useForm({
 *     defaultValues: {
 *     firstName: "firstName"
 *   }});
 *   const { dirtyFields } = useFormState({
 *     control
 *   });
 *   const onSubmit = (data) => console.log(data);
 *
 *   return (
 *     <form onSubmit={handleSubmit(onSubmit)}>
 *       <input {...register("firstName")} placeholder="First Name" />
 *       {dirtyFields.firstName && <p>Field is dirty.</p>}
 *       <input type="submit" />
 *     </form>
 *   );
 * }
 * ```
 */
export function useFormState<TFieldValues extends FieldValues = FieldValues>(
    props?: UseFormStateProps<TFieldValues>,
): UseFormStateReturn<TFieldValues> {
    const formContext = useFormContext();
    const { form = formContext } = props ?? {};

    if (!form) {
        throw new Error(
            'Form is not provided, either pass the form in props or wrap you form inside FormProvider',
        );
    }

    return form.formState$.get() as UseFormStateReturn<TFieldValues>;
}
